import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, AlertTriangle, ArrowRight } from "lucide-react";
import AppShell from "../components/AppShell";
import LegalFooter from "../components/LegalFooter";
import { api } from "../lib/api";
import { useEntitlements, invalidateEntitlements } from "../lib/entitlements";

/**
 * Payment result pages — /checkout/success, /pending, /failure.
 * The URL variant only controls copy; entitlement truth ALWAYS comes from the
 * backend snapshot polled below.
 */
export default function PaymentResultPage({ variant }) {
  const [search] = useSearchParams();
  const ref = search.get("ref") || "";
  const { status, refresh } = useEntitlements();
  const [payments, setPayments] = useState([]);
  const [polling, setPolling] = useState(variant === "success" || variant === "pending");

  // Poll /payments/me until we see an activated payment for this ref, up to 30s.
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    let tries = 0;
    async function tick() {
      try {
        invalidateEntitlements();
        await refresh();
        const r = await api.get("/payments/me", { withCredentials: true });
        if (cancelled) return;
        setPayments(r.data?.recent_payments || []);
        const matched = (r.data?.recent_payments || []).find((p) => p.id === ref);
        if (matched?.activated_at || r.data?.subscription?.is_paid) { setPolling(false); return; }
      } catch (_e) { /* keep polling */ }
      tries += 1;
      if (tries < 12) setTimeout(tick, 2500);
      else setPolling(false);
    }
    tick();
    return () => { cancelled = true; };
  }, [polling, ref, refresh]);

  const config = {
    success: { Icon: CheckCircle2, color: "#10b981", title: "¡Listo! Tu suscripción está activa.",
      body: "Ya puedes usar Mi Contador con todas las herramientas de tu plan." },
    pending: { Icon: Clock, color: "#f59e0b", title: "Tu pago está siendo procesado.",
      body: "En cuanto Mercado Pago confirme el pago activaremos tu suscripción y te enviaremos un correo." },
    failure: { Icon: XCircle, color: "#ef4444", title: "No pudimos procesar tu pago.",
      body: "Verifica los datos e intenta de nuevo. Tu cuenta y tu información siguen a salvo." },
    cancelled: { Icon: AlertTriangle, color: "#94a3b8", title: "El proceso de pago fue cancelado.",
      body: "No se realizó ningún cobro. Puedes volver a elegir un plan cuando quieras." },
  }[variant] || {};
  const { Icon, color, title, body } = config;

  return (
    <AppShell>
      <div className="pricing-page" data-testid={`payment-result-${variant}`}>
        <section className="trial-expired-hero" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)", borderColor: "#e2e8f0" }}>
          <div className="trial-expired-icon" style={{ background: `${color}22`, color }} aria-hidden="true">
            <Icon size={28} />
          </div>
          <small className="eyebrow" style={{ color }}>ESTADO DE PAGO</small>
          <h1 style={{ marginTop: 10 }}>{title}</h1>
          <p style={{ maxWidth: 620, margin: "8px auto 0" }}>{body}</p>

          {ref && <div style={{ marginTop: 14, font: '600 12px "Nunito", sans-serif', color: "#64748b" }}>
            Referencia: <code>{ref}</code>
          </div>}

          {status?.is_paid && variant === "success" && (
            <div className="pricing-notice success" style={{ marginTop: 16 }} data-testid="payment-plan-summary">
              Plan activo: <b>{status.plan_name}</b>{status.next_billing_date && <> · Próximo cobro: <b>{String(status.next_billing_date).slice(0,10)}</b></>}
              {status.access_expires_at && !status.next_billing_date && <> · Acceso hasta <b>{String(status.access_expires_at).slice(0,10)}</b></>}
            </div>
          )}
          {polling && variant !== "failure" && variant !== "cancelled" && (
            <div className="pricing-notice" style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#1e3a8a", marginTop: 16 }} data-testid="payment-polling">
              Verificando el pago con Mercado Pago… esto puede tardar hasta 30 segundos.
            </div>
          )}

          <div style={{ display: "inline-flex", gap: 10, marginTop: 22, flexWrap: "wrap", justifyContent: "center" }}>
            <Link to="/" className="pricing-cta primary" data-testid="payment-goto-home">Ir a Mi Contador <ArrowRight size={14} /></Link>
            {(variant === "failure" || variant === "cancelled") && (
              <Link to="/pricing" className="pricing-cta" data-testid="payment-retry">Intentar nuevamente</Link>
            )}
          </div>
        </section>
        <LegalFooter variant="pricing" />
      </div>
    </AppShell>
  );
}
